import { useEffect, useCallback, useRef, useState } from "react";

interface AccessibilityPreferences {
	prefersReducedMotion: boolean;
	prefersHighContrast: boolean;
	prefersDarkMode: boolean;
	fontSize: "small" | "medium" | "large";
	focusRingVisible: boolean;
}

export function useAccessibility() {
	const [preferences, setPreferences] = useState<AccessibilityPreferences>({
		prefersReducedMotion: false,
		prefersHighContrast: false,
		prefersDarkMode: false,
		fontSize: "medium",
		focusRingVisible: false,
	});

	const announceRef = useRef<HTMLDivElement | null>(null);

	// Create or get the screen reader announcement element
	const getAnnouncementElement = useCallback(() => {
		if (announceRef.current) return announceRef.current;

		if (typeof window === "undefined") return null;

		let announcer = document.getElementById("sr-announcer") as HTMLDivElement;
		if (!announcer) {
			announcer = document.createElement("div");
			announcer.id = "sr-announcer";
			announcer.setAttribute("aria-live", "polite");
			announcer.setAttribute("aria-atomic", "true");
			announcer.className = "sr-only";
			announcer.style.cssText = `
				position: absolute !important;
				width: 1px !important;
				height: 1px !important;
				padding: 0 !important;
				margin: -1px !important;
				overflow: hidden !important;
				clip: rect(0, 0, 0, 0) !important;
				white-space: nowrap !important;
				border: 0 !important;
			`;
			document.body.appendChild(announcer);
		}

		announceRef.current = announcer;
		return announcer;
	}, []);

	// Announce messages to screen readers
	const announce = useCallback((message: string, priority: "polite" | "assertive" = "polite") => {
		const announcer = getAnnouncementElement();
		if (!announcer) return;

		// Clear previous announcement
		announcer.textContent = "";
		announcer.setAttribute("aria-live", priority);

		// Use setTimeout to ensure screen readers pick up the change
		setTimeout(() => {
			announcer.textContent = message;
		}, 100);

		// Clear after announcement
		setTimeout(() => {
			announcer.textContent = "";
		}, 1000);
	}, [getAnnouncementElement]);

	// Focus management
	const focusElement = useCallback((selector: string | HTMLElement, delay = 0) => {
		setTimeout(() => {
			const element = typeof selector === "string" 
				? document.querySelector(selector) as HTMLElement
				: selector;

			if (element && element.focus) {
				element.focus();
				
				// Scroll into view if needed
				element.scrollIntoView({
					behavior: preferences.prefersReducedMotion ? "auto" : "smooth",
					block: "center",
				});
			}
		}, delay);
	}, [preferences.prefersReducedMotion]);

	// Skip link functionality
	const addSkipLink = useCallback((targetId: string, text: string = "Skip to main content") => {
		if (typeof window === "undefined") return;

		const existingSkipLink = document.getElementById("skip-link");
		if (existingSkipLink) return;

		const skipLink = document.createElement("a");
		skipLink.id = "skip-link";
		skipLink.href = `#${targetId}`;
		skipLink.textContent = text;
		skipLink.className = "sr-only focus:not-sr-only focus:absolute focus:top-4 focus:start-4 bg-primary text-primary-foreground px-4 py-2 rounded-md z-50";
		
		skipLink.addEventListener("click", (e) => {
			e.preventDefault();
			const target = document.getElementById(targetId);
			if (target) {
				target.focus();
				target.scrollIntoView({
					behavior: preferences.prefersReducedMotion ? "auto" : "smooth",
				});
			}
		});

		document.body.insertBefore(skipLink, document.body.firstChild);
	}, [preferences.prefersReducedMotion]);

	// Enhanced keyboard navigation
	const trapFocus = useCallback((container: HTMLElement) => {
		const focusableSelectors = [
			'button',
			'[href]',
			'input',
			'select',
			'textarea',
			'[tabindex]:not([tabindex="-1"])',
			'[contenteditable="true"]'
		].join(',');

		const focusableElements = container.querySelectorAll(focusableSelectors) as NodeListOf<HTMLElement>;
		const firstElement = focusableElements[0];
		const lastElement = focusableElements[focusableElements.length - 1];

		const handleTabKey = (e: KeyboardEvent) => {
			if (e.key !== 'Tab') return;

			if (e.shiftKey) {
				if (document.activeElement === firstElement) {
					e.preventDefault();
					lastElement.focus();
				}
			} else {
				if (document.activeElement === lastElement) {
					e.preventDefault();
					firstElement.focus();
				}
			}
		};

		const handleEscapeKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				const closeButton = container.querySelector('[data-dismiss], [aria-label*="close"]') as HTMLElement;
				if (closeButton) {
					closeButton.click();
				}
			}
		};

		container.addEventListener('keydown', handleTabKey);
		container.addEventListener('keydown', handleEscapeKey);

		// Focus first element
		if (firstElement) {
			firstElement.focus();
		}

		// Return cleanup function
		return () => {
			container.removeEventListener('keydown', handleTabKey);
			container.removeEventListener('keydown', handleEscapeKey);
		};
	}, []);

	// Progress announcements
	const announceProgress = useCallback((current: number, total: number, description: string = "Processing") => {
		const percentage = Math.round((current / total) * 100);
		const message = `${description}: ${percentage}% complete`;
		
		// Only announce at 10% intervals to avoid spam
		if (percentage % 10 === 0) {
			announce(message, "polite");
		}
	}, [announce]);

	// Error announcements
	const announceError = useCallback((error: string) => {
		announce(`Error: ${error}`, "assertive");
	}, [announce]);

	// Success announcements
	const announceSuccess = useCallback((message: string) => {
		announce(`Success: ${message}`, "polite");
	}, [announce]);

	// Detect accessibility preferences
	useEffect(() => {
		if (typeof window === "undefined") return;

		const mediaQueries = {
			prefersReducedMotion: window.matchMedia("(prefers-reduced-motion: reduce)"),
			prefersHighContrast: window.matchMedia("(prefers-contrast: high)"),
			prefersDarkMode: window.matchMedia("(prefers-color-scheme: dark)"),
		};

		const updatePreferences = () => {
			setPreferences(prev => ({
				...prev,
				prefersReducedMotion: mediaQueries.prefersReducedMotion.matches,
				prefersHighContrast: mediaQueries.prefersHighContrast.matches,
				prefersDarkMode: mediaQueries.prefersDarkMode.matches,
			}));
		};

		// Initial check
		updatePreferences();

		// Listen for changes
		Object.values(mediaQueries).forEach(mq => {
			mq.addEventListener("change", updatePreferences);
		});

		// Keyboard navigation detection
		const handleFirstTab = (e: KeyboardEvent) => {
			if (e.key === "Tab") {
				setPreferences(prev => ({ ...prev, focusRingVisible: true }));
				document.body.classList.add("keyboard-navigation");
			}
		};

		const handleMouseDown = () => {
			setPreferences(prev => ({ ...prev, focusRingVisible: false }));
			document.body.classList.remove("keyboard-navigation");
		};

		document.addEventListener("keydown", handleFirstTab);
		document.addEventListener("mousedown", handleMouseDown);

		// Cleanup
		return () => {
			Object.values(mediaQueries).forEach(mq => {
				mq.removeEventListener("change", updatePreferences);
			});
			document.removeEventListener("keydown", handleFirstTab);
			document.removeEventListener("mousedown", handleMouseDown);
		};
	}, []);

	// Apply accessibility preferences to DOM
	useEffect(() => {
		if (typeof window === "undefined") return;

		const root = document.documentElement;

		// Apply reduced motion
		if (preferences.prefersReducedMotion) {
			root.style.setProperty("--animation-duration", "0.01ms");
			root.style.setProperty("--transition-duration", "0.01ms");
		} else {
			root.style.removeProperty("--animation-duration");
			root.style.removeProperty("--transition-duration");
		}

		// Apply high contrast
		if (preferences.prefersHighContrast) {
			root.classList.add("high-contrast");
		} else {
			root.classList.remove("high-contrast");
		}

		// Apply focus ring visibility
		if (preferences.focusRingVisible) {
			root.classList.add("focus-visible");
		} else {
			root.classList.remove("focus-visible");
		}
	}, [preferences]);

	return {
		preferences,
		announce,
		announceProgress,
		announceError,
		announceSuccess,
		focusElement,
		addSkipLink,
		trapFocus,
	};
}